import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Share } from "lucide-react";
import { useAuth } from "@/contexts/auth";
import { generateAnonymousId } from "@/utils/anonymousId";
import { recordShare, fetchSharesCount } from "@/services/shareService";
import { ContentType } from "@/services/comments/types";

interface ShareButtonProps {
  contentId: string;
  contentType?: ContentType;
  initialShares?: number;
  showCount?: boolean;
  size?: "sm" | "md" | "lg";
  onShareClick?: () => void;
  title?: string;
  description?: string;
  url?: string;
}

const ShareButton: React.FC<ShareButtonProps> = ({
  contentId,
  contentType = "card",
  initialShares = 0,
  showCount = true,
  size = "md",
  onShareClick,
  title,
  description,
  url,
}) => {
  const { user, isAuthenticated } = useAuth();
  const [sharesCount, setSharesCount] = useState(initialShares);
  const [anonymousId, setAnonymousId] = useState<string | null>(null);

  // Fetch the latest share count when the component mounts
  useEffect(() => {
    const loadShareCount = async () => {
      try {
        const count = await fetchSharesCount(contentId, contentType);
        setSharesCount(count);
      } catch (error) {
        console.error("Error fetching share count:", error);
      }
    };

    loadShareCount();

    // Generate anonymous ID for non-authenticated users
    if (!isAuthenticated) {
      generateAnonymousId().then((id) => setAnonymousId(id));
    }
  }, [contentId, contentType, isAuthenticated]);

  // Handle share
  const handleShare = async () => {
    // Custom share handler if provided
    if (onShareClick) {
      onShareClick();
      return;
    }

    try {
      // Default sharing behavior
      let currentAnonymousId = anonymousId;
      if (!isAuthenticated && !currentAnonymousId) {
        currentAnonymousId = await generateAnonymousId();
        setAnonymousId(currentAnonymousId);
      }

      // Track share with the service
      await recordShare(
        contentId,
        isAuthenticated ? user?.id || null : null,
        !isAuthenticated ? currentAnonymousId : null,
        "direct",
        contentType
      );

      // Update local count
      setSharesCount((prev) => prev + 1);

      // Navigate to the share URL or copy to clipboard
      const shareUrl = url || window.location.href;
      const shareTitle = title || document.title;
      const shareDescription =
        description || `Check out this ${contentType} on NationCard™!`;

      // Try to use the Web Share API if available
      if (navigator.share) {
        await navigator.share({
          title: shareTitle,
          text: shareDescription,
          url: shareUrl,
        });
        // Toast notification removed
      } else {
        // Fallback to copying to clipboard
        await navigator.clipboard.writeText(shareUrl);
        // Toast notification removed
      }
    } catch (error: any) {
      console.error("Error sharing:", error);
      if (error.name !== "AbortError") {
        // Toast notification removed
      }
    }
  };

  // Size variants
  const sizeClasses = {
    sm: "h-8 px-3 text-xs",
    md: "h-9 px-4",
    lg: "h-10 px-6",
  };

  return (
    <Button
      variant="outline"
      className={`${sizeClasses[size]}`}
      onClick={handleShare}
      size={size === "sm" ? "sm" : size === "lg" ? "lg" : "default"}
    >
      <Share className="mr-1 h-4 w-4" />
      {showCount && <span>{sharesCount}</span>}
    </Button>
  );
};

export default ShareButton;
